import type { SizeVariant, WidgetDefinition, WidgetKind } from "./types";

interface WidgetLibraryProps {
  widgetDefs: Record<string, WidgetDefinition>;
  activeTypes: string[];
  onAdd: (type: string) => void;
  onClose: () => void;
}

const KIND_LABELS: Record<WidgetKind, string> = {
  chart: "Charts",
  value: "Stats & lists",
};

const SIZE_ORDER: SizeVariant[] = ["xs", "s", "m", "l"];

function widgetKind(def: WidgetDefinition): WidgetKind {
  return def.kind ?? "value";
}

function availableSizeLabels(def: WidgetDefinition): string[] {
  return SIZE_ORDER.filter((v) => def.sizes[v] !== undefined).map((v) =>
    v.toUpperCase(),
  );
}

export function WidgetLibrary({
  widgetDefs,
  activeTypes,
  onAdd,
  onClose,
}: WidgetLibraryProps) {
  const available = Object.values(widgetDefs).filter(
    (def) => !def.hideFromLibrary,
  );

  // Group strictly by kind (chart vs value) so graphs and non-graphs are
  // visually separated in the picker — the user asked for this because mixed
  // categories made it hard to find a graph widget at a glance.
  const grouped: Record<WidgetKind, WidgetDefinition[]> = {
    chart: [],
    value: [],
  };
  for (const def of available) grouped[widgetKind(def)].push(def);

  return (
    <aside className="widget-library">
      <header className="widget-library__header">
        <div>
          <h3>Add widgets</h3>
        </div>
        <button
          type="button"
          className="icon-button"
          onClick={onClose}
          aria-label="Close widget library"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </header>
      <div className="widget-library__body">
        {(["chart", "value"] as WidgetKind[]).map((kind) => {
          const items = grouped[kind];
          if (!items.length) return null;
          return (
            <section key={kind} className="widget-library__section">
              <h4 className="widget-library__section-title">
                {KIND_LABELS[kind]} · {items.length}
              </h4>
              <ul className="widget-library__list">
                {items.map((def) => {
                  const isActive = activeTypes.includes(def.type);
                  const sizeLabels = availableSizeLabels(def);
                  return (
                    <li key={def.type} className="widget-library__item">
                      <div className="widget-library__item-main">
                        {def.Icon && (
                          <def.Icon className="widget-library__item-icon" />
                        )}
                        <div className="widget-library__item-text">
                          <p className="widget-library__item-title">
                            {def.title}
                          </p>
                          <p className="widget-library__item-desc">
                            {def.description}
                          </p>
                          <div
                            className="widget-library__sizes"
                            aria-label="Available sizes"
                          >
                            {sizeLabels.map((label) => (
                              <span
                                key={label}
                                className="widget-library__size-chip"
                                title={`Available in ${label} size`}
                              >
                                {label}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className={`button compact ${isActive ? "ghost" : "primary"}`}
                        onClick={() => !isActive && onAdd(def.type)}
                        disabled={isActive}
                      >
                        {isActive ? "Added" : "Add"}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </aside>
  );
}
